import {
  doc,
  query,
  getDoc,
  setDoc,
  addDoc,
  getDocs,
  deleteDoc,
  collection,
  onSnapshot,
  DocumentData,
  QuerySnapshot,
  QueryConstraint,
  DocumentSnapshot,
  WithFieldValue,
  SetOptions,
} from 'firebase/firestore';

import { db } from 'config/firebase';
import { FirebaseError } from 'firebase/app';

interface Iterable {
  id: string;
}

export type FirebaseQuery = QuerySnapshot<DocumentData>;

export type FirebaseDocument = DocumentSnapshot<DocumentData>;

interface Request {
  collection: string;
}

interface GetDocRequest extends Request {
  id: string;
}

interface GetDocsRequest extends Request {
  constraints?: QueryConstraint[];
}

interface CreateDoc extends Request {
  id?: string;
  data: WithFieldValue<DocumentData>;
  options?: SetOptions;
}

interface QuerySubscribe {
  onSnapshot: (snapshot: FirebaseQuery) => void;
  onError?: (error: FirebaseError) => void;
}

interface DocSubscribe {
  onSnapshot: (snapshot: FirebaseDocument) => void;
  onError?: (error: FirebaseError) => void;
}

export const createQuery = (props: GetDocsRequest) => {
  const collectionRef = collection(db, props.collection);
  const constraints = props.constraints || [];
  return query(collectionRef, ...constraints);
};

export const getDocument = (props: GetDocRequest) => {
  const ref = doc(db, props.collection, props.id);
  return getDoc(ref);
};

export const getDocuments = (props: GetDocsRequest) => {
  return getDocs(createQuery(props));
};

export const createDocument = (props: CreateDoc) => {
  const collectionRef = collection(db, props.collection);
  return addDoc(collectionRef, props.data);
};

export const createDocumentWithID = (props: CreateDoc & { id: string }) => {
  const docRef = doc(db, props.collection, props.id);
  return setDoc(docRef, props.data);
};

export const updateDocument = (props: CreateDoc & { id: string }) => {
  const collectionRef = collection(db, props.collection);
  const document = doc(collectionRef, props.id);
  return setDoc(document, props.data, { merge: true, ...props.options });
};

export const deleteDocument = (
  props: Pick<CreateDoc, 'collection'> & { id?: string }
) => {
  const collectionRef = collection(db, props.collection);
  return deleteDoc(doc(collectionRef, props.id));
};

export const subscribeQuery = ({
  onError,
  onSnapshot: onSnapshotCallback,
  ...queryProps
}: GetDocsRequest & QuerySubscribe) =>
  onSnapshot(createQuery(queryProps), onSnapshotCallback, onError);

export const subscribeDocument = (props: DocSubscribe & GetDocRequest) =>
  onSnapshot(
    doc(db, props.collection, props.id),
    props.onSnapshot,
    props.onError
  );

export const castDocs = <T extends Iterable>(list: FirebaseQuery) => {
  return list.docs.map((item) => ({ id: item.id, ...item.data() } as T));
};

export const castDoc = <T extends Iterable>(document: FirebaseDocument) => {
  return { id: document.id, ...document.data() } as T;
};
